// src/pages/reports/reservas/DistribucionReservasPage.tsx
import { useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useTranslation } from "react-i18next";
import {
  useReservasDistribucionReport,
  useDistribucionAlumnosReport,
} from "../../../hooks/useReportJob";
import type { DistribRow } from "../../../api/reports";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

const CAMPOS = [
  { value: "curso", label: "Curso" },
  { value: "proyecto", label: "Proyecto" },
  { value: "formato", label: "Formato" },
  { value: "centro", label: "Centro" },
  { value: "estado", label: "Estado" },
] as const;

type Campo = (typeof CAMPOS)[number]["value"];

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#845EC2",
  "#4D8076",
  "#F65A83",
  "#2C73D2",
];


export default function DistribucionReservasPage() {
  const { t } = useTranslation<"translation">();
  const [campo, setCampo] = useState<Campo>("curso");
  const [desde, setDesde] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(dayjs().format("YYYY-MM-DD"));

  // ─────────────── Job 1: distribución (pie) ────────────────
  const { run, useStatus } = useReservasDistribucionReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  // ─────────────── Job 2: alumnos de un segmento ────────────
  const { run: runAlumnos, useStatus: useStatusAlumnos } = useDistribucionAlumnosReport();
  const [jobIdAlu, setJobIdAlu] = useState<string>();
  const { data: statusAlu } = useStatusAlumnos(jobIdAlu);

  const [segmento, setSegmento] = useState<string | null>(null);

  const lanzar = async () => {
    setSegmento(null); // reset posibles tablas anteriores
    const { jobId } = await run({ campo, desde, hasta });
    setJobId(jobId);
  };

  const handleSliceClick = (entry: DistribRow) => {
    if (!entry?.label) return;
    // evita doble clic mientras esté generando
    if (statusAlu && !statusAlu.done && !statusAlu.failed) return;

    setSegmento(entry.label);
    runAlumnos({ campo, valor: entry.label, desde, hasta }).then(({ jobId }) => setJobIdAlu(jobId));
  };

  const handleDownload = async () => {
    if (res?.url) {
      try {
        await downloadReportFile(res.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  const handleDownloadAlu = async () => {
    if (resAlu?.url) {
      try {
        await downloadReportFile(resAlu.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  const res = status?.result;
  const resAlu = statusAlu?.result;

  return (
    <Stack spacing={3}>
      {/* ────────────── Título ────────────── */}
      <Typography variant="h5">
        {t("distribucionReservas.titulo", {
          campo: t(`distribucionReservas.campos.${campo}`).toLowerCase(),
        })}
      </Typography>

      {/* ────────────── Filtros ───────────── */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t("distribucionReservas.filtros.campo")}</InputLabel>
          <Select value={campo} label="Campo" onChange={(e) => setCampo(e.target.value as Campo)}>
            {CAMPOS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {t(`distribucionReservas.campos.${c.value}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          type="date"
          size="small"
          label={t("distribucionReservas.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          size="small"
          label={t("distribucionReservas.filtros.hasta")}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <Button variant="contained" onClick={lanzar}>
          {t("distribucionReservas.filtros.generar")}
        </Button>
      </Stack>

      {/* ────────────── Progreso gráfico ───────────── */}
      {status && !status.done && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={24} />
          <Typography>{t("distribucionReservas.estado.generando")}</Typography>
        </Stack>
      )}

      {/* ────────────── Resultados gráfico ─────────── */}
      {status?.done && res && Array.isArray(res.rows) && res.rows.length > 0 && (
        <>
          <Alert severity="success" sx={{ width: "fit-content" }}>
            XLSX:&nbsp;
            <Button
              variant="text"
              size="small"
              onClick={handleDownload}
              sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
            >
              {t("distribucionReservas.estado.descargar")}
            </Button>
          </Alert>

          <Paper sx={{ p: 2, height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={res.rows as DistribRow[]}
                  dataKey="n"
                  nameKey="label"
                  label={({ percent = 0 }) => `${(percent * 100).toFixed(0)}%`}
                  outerRadius={120}
                  innerRadius={60}
                  isAnimationActive={false}
                  onClick={(_, index) => handleSliceClick((res.rows as DistribRow[])[index])}
                  cursor="pointer"
                >
                  {res.rows.map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      {/* ────────────── Progreso alumnos ───────────── */}
      {segmento && statusAlu && !statusAlu.done && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography>
            {t("distribucionReservas.estado.generandoLista", { seg: segmento })}
          </Typography>
        </Stack>
      )}

      {/* ────────────── Tabla alumnos ─────────────── */}
      {statusAlu?.done && resAlu && Array.isArray(resAlu.rows) && (
        <>
            <Alert severity="info" sx={{ width: "fit-content" }}>
              XLSX:&nbsp;
              <Button
                variant="text"
                size="small"
                onClick={handleDownloadAlu}
                sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
              >
                {t("distribucionReservas.estado.descargar")}
              </Button>
            </Alert>

          <Paper sx={{ mt: 2, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("distribucionReservas.lista.titulo", { seg: segmento })}
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("alumnos.nombre")}</TableCell>
                  <TableCell>{t("alumnos.email")}</TableCell>
                  <TableCell>{t("alumnos.telefono")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resAlu.rows.map((a: any) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.nombre}</TableCell>
                    <TableCell>{a.email}</TableCell>
                    <TableCell>{a.telefono}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* Sin datos */}
      {status?.done && (!res || !Array.isArray(res.rows) || res.rows.length === 0) && (
        <Typography variant="body2" color="text.secondary">
          {t("distribucionReservas.estado.sinDatos")}
        </Typography>
      )}
    </Stack>
  );
}
